import { useMemo } from 'react';

import { DefaultError } from '@tanstack/react-query';

import { Node, Workload } from '@cast/types';

import { useSimulatedClusterDemoUi } from '@cast/infrastructure-demo';
import { makeAddNodeDependencies } from 'features/cluster/nodes/_utils/makeAddNodeDependencies';
import { NodeWithDependencies } from 'features/cluster/nodes/types';
import { useCorrectRebalanceWorkloadsQuery } from 'features/cluster/rebalance/_hooks/useCorrectRebalanceWorkloadsQuery';
import { useIsKentRebalancingMode } from 'features/cluster/rebalance/_hooks/useIsKentRebalancingMode';
import { useGetKentRebalancingConstraintsQuery } from 'hooks/queries/kent';
import { useProblematicNodesQuery } from 'hooks/queries/nodes/useProblematicNodesQuery';
import { useClusterNodesPricingQuery } from 'hooks/queries/pricing';

import { useCluster } from '@cast/shared-cluster-context-data-access';

export type UseNodeDependenciesArgs = {
  clusterId?: string;
  withPricing?: boolean;
  withRebalancingWorkloads?: boolean;
  withIssues?: boolean;
  refetchInterval?: number | false;
  workloadsRefetchInterval?: number | false;
  demoFetchIndex?: number;
  syncGroup?: string;
};

export type UseNodeDependencies = {
  workloads?: Workload[];
  isPricingLoading: boolean;
  pricingError?: DefaultError;
  isWorkloadsLoading: boolean;
  workloadsError?: DefaultError;
  isProblematicNodesLoading: boolean;
  problematicNodesError?: DefaultError;
  kentRebalancingConstraintsError?: unknown;
  hasResultsWithError: boolean;
  addNodeDependencies?: (node: Node) => NodeWithDependencies;
  refetch: () => void;
};

export const useNodeDependencies = ({
  clusterId: _clusterId,
  withPricing = false,
  withRebalancingWorkloads = false,
  withIssues = false,
  refetchInterval,
  workloadsRefetchInterval,
  demoFetchIndex,
  syncGroup,
}: UseNodeDependenciesArgs = {}): UseNodeDependencies => {
  const { cluster } = useCluster();
  const clusterId = _clusterId || cluster.id;
  const isSimulatedClusterDemoUi = useSimulatedClusterDemoUi();
  const isKentRebalancingMode = useIsKentRebalancingMode();

  const {
    nodesPricing,
    isLoading: isPricingLoading,
    error: pricingError,
    refetch: refetchPricing,
  } = useClusterNodesPricingQuery(clusterId, {
    refetchInterval,
    enabled: withPricing,
    demoFetchIndex,
    syncGroup,
  });

  const {
    workloads,
    isLoading: isWorkloadsLoading,
    error: workloadsError,
    refetch: refetchWorkloads,
  } = useCorrectRebalanceWorkloadsQuery({
    clusterId,
    enabled: withRebalancingWorkloads,
    refetchInterval: workloadsRefetchInterval,
  });

  const {
    data: problematicNodesResponse,
    isLoading: isProblematicNodesLoading,
    error: problematicNodesError,
    refetch: refetchProblematicNodes,
  } = useProblematicNodesQuery(clusterId, {
    refetchInterval,
    enabled:
      // In Kent rebalancing mode node problems come from the rebalancing-constraints endpoint
      !isKentRebalancingMode &&
      !isSimulatedClusterDemoUi &&
      !!clusterId &&
      withIssues &&
      cluster.isPhase2,
  });

  const {
    data: kentRebalancingConstraints,
    isLoading: isKentRebalancingConstraintsLoading,
    error: kentRebalancingConstraintsError,
    refetch: refetchKentRebalancingConstraints,
  } = useGetKentRebalancingConstraintsQuery({
    enabled: withIssues && isKentRebalancingMode,
    request: { clusterId },
  });

  const addNodeDependencies = useMemo(() => {
    return makeAddNodeDependencies({
      nodesPricing,
      problematicNodes: problematicNodesResponse?.nodes,
      workloads,
      isKentRebalancingMode,
      kentRebalancingConstraints,
    });
  }, [
    isKentRebalancingMode,
    nodesPricing,
    problematicNodesResponse?.nodes,
    workloads,
    kentRebalancingConstraints,
  ]);

  return {
    workloads,
    isPricingLoading,
    pricingError: pricingError || undefined,
    isWorkloadsLoading,
    workloadsError,
    isProblematicNodesLoading:
      isProblematicNodesLoading || isKentRebalancingConstraintsLoading,
    problematicNodesError:
      problematicNodesError || kentRebalancingConstraintsError || undefined,
    kentRebalancingConstraintsError:
      kentRebalancingConstraintsError || undefined,
    hasResultsWithError: Boolean(
      pricingError ||
        workloadsError ||
        problematicNodesError ||
        kentRebalancingConstraintsError
    ),
    addNodeDependencies,
    refetch: () => {
      if (pricingError) {
        refetchPricing();
      }
      if (workloadsError) {
        refetchWorkloads();
      }
      if (problematicNodesError) {
        refetchProblematicNodes();
      }
      if (kentRebalancingConstraintsError) {
        refetchKentRebalancingConstraints();
      }
    },
  };
};
